import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAnnouncement } from '../hooks/useAnnouncement';
import type { AnnouncementConfig } from '../types';

interface AnnouncementBannerProps {
  announcement: AnnouncementConfig;
}

/**
 * Compact, dismissible banner for persistent feature announcements.
 * Displays below the header with a subtle lime accent.
 */
export function AnnouncementBanner({ announcement }: AnnouncementBannerProps) {
  const navigate = useNavigate();
  const { isVisible, dismiss, trackImpression, trackCtaClick } = useAnnouncement(announcement, 'banner');
  const { content } = announcement;

  const shouldShow = isVisible;

  // Track impression when banner becomes visible
  useEffect(() => {
    if (shouldShow) {
      trackImpression('banner');
    }
  }, [shouldShow, trackImpression]);

  const handlePrimaryCta = () => {
    trackCtaClick('primary');

    if (content.cta?.to) {
      navigate(content.cta.to);
    } else if (content.cta?.href) {
      window.open(content.cta.href, '_blank', 'noopener,noreferrer');
    } else if (content.cta?.onClick) {
      content.cta.onClick();
    }
  };

  const handleDismiss = async () => {
    await dismiss();
  };

  if (!shouldShow) {
    return null;
  }

  return (
    <div className="relative flex w-full items-center justify-between gap-4 border-b border-lime/20 bg-lime/5 px-4 py-2">
      <div className="flex items-center gap-3">
        <Sparkles className="h-4 w-4 shrink-0 text-lime" />
        <span className="text-sm font-medium text-white">
          <span className="font-semibold text-lime">{content.title}</span>
          <span className="mx-2 text-white/40">—</span>
          <span className="text-white/80">{content.description}</span>
        </span>
      </div>

      <div className="flex items-center gap-2">
        {content.cta && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePrimaryCta}
            className="h-7 text-lime hover:bg-lime/10 hover:text-lime"
          >
            {content.cta.label}
          </Button>
        )}
        <button
          onClick={handleDismiss}
          className="rounded p-1 text-white/40 transition-colors hover:bg-white/10 hover:text-white/60"
          aria-label="Dismiss announcement"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
